<template lang="pug">	
	.maps--zips-map.flex-col(:style="{ '--top-offset': `${$el && $el.offsetTop || 0}px` }")
		.map-holder(:class="{ 'has-zips': zipData.length , 'has-filters': zipData.length }")
			.map(ref="map")
			.actions-group.flex-row.space-large(v-show="actionsVisible && $scopedSlots['search-actions']").align-start
				slot(name="search-actions")
			.buttons-group(v-show="actionsVisible")
				slot(name="actions")
			.toolbar-actions.leaflet-control.leaflet-bar(ref="toolbarActions")
				a.action.flex-row.align-center.center(:class="{ active: selectionMode === 'filter' }", @click="selectionMode = 'filter'", :title="$tCaption('Filter zip-codes')")
					fas(icon="filter")
				a.action.flex-row.align-center.center(:class="{ active: selectionMode === 'eraser' }", @click="selectionMode = 'eraser'", :title="$tCaption('Remove zip-codes')")
					fas(icon="eraser")
			slot(name="map")
			Loader(v-if="isLoading", text="", :fullscreen="true")
			.bottom-panel.flex-row.space(v-if="zipData.length")
				.auto-style.flex-row.space-small
					span {{$tCaption('Auto-style by')}}
					.accent-color {{autoStyleFieldCaption}}
				.flex-row.space-small.align-center(v-if="makingScreenshot")
					fas(icon="spinner", spin="true")
					span {{$tCaption('Processing')}}
		.statistics(v-if="zipData.length")
			DxScrollView(showScrollbar="always", height="calc(40vh - var(--top-offset) / 2)")
				.content
					StatisticsInfo(v-if="config", :statistics="statistics", :config="config")
		Popup(v-if="activeZip", :popupVisible="!!activeZip", @hidden="activeZip = null", :title="activeZip.ZipCode", :width="500")
			.flex-col.space-medium
				.flex-row.space(v-for="item in plainItemsGouped")
					template(v-if="item.type === 'group'")
						.flex-col.space
							.font-big.upper.grey.group {{ item.caption }}
							.flex-row.space(v-for="item in item.items")
								.grey {{ item.caption }}:
								.value {{ formatter.format(activeZip.Props[item.fieldName] || 0) }}
					template(v-else)
						.grey {{ item.caption }}:
						.value {{ formatter.format(activeZip.Props[item.fieldName] || 0) }}
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close', '@actions')", @click="activeZip = null")
			template(slot="actions-left")
				DxButton(v-if="hasSelection", :type="activeZip.selected ? 'warning' : 'default'", :text="activeZip.selected ? $tCaption('Remove', '@actions') : $tCaption('Add', '@actions')", @click="toggleZipSelection(activeZip)")
		MapScreener(v-if="makingScreenshot", :geoJsons="zipData.filter(f => !hasSelection || f.selected).map(f => f.geoJson)", @image-generated="onImageGenerated", :width="1920 / 2", :height="1280 / 2")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import dynamic from 'dynamic';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import resizeObserver from 'resize-observer';
	import circleToPolygon from 'circle-to-polygon';
	import wkt from 'wicket';
	import Loader from 'app-core/Loader.vue';
	import parseWkt from 'wellknown';
	import intersect from '@turf/intersect';
	import StatisticsInfo from './StatisticsInfo.vue';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import Popup from 'app-core/Popup.vue';
	import MapScreener from './MapScreener.vue';

	export default {
		translationCategory: 'maps.ZipsMap',
		name: 'maps--zips-map',
		components: { DxTextBox, DxButton, DxSelectBox, Loader, StatisticsInfo, DxScrollView, Popup, MapScreener },
		props: {
			actionsVisible: VueTypes.bool.def(false),
			countryCode: VueTypes.string.def('US'),
			mapPosition: VueTypes.object.def({}),
			showFilters: VueTypes.bool.def(true),
			locations: VueTypes.any.def([]),
			readonly: VueTypes.bool.def(false),
			selectedZips: VueTypes.any.def(null),
			config: VueTypes.any.def(null)
		},
		data() {
			const formatter = new Intl.NumberFormat('en-US', {
				style: 'decimal'
			});
			return {
				formatter,
				autocomplete: null,
				searchText: null,
				filtersData: [],
				zipData: [],
				isLoading: 0,
				filterAreaWkts: null,
				styles: {
					highlight: {
						fillColor: '#FF5722',
						fillOpacity: 0.7,
						stroke: true,
						fill: true,
						color: '#333',
						opacity: 1,
						weight: 2,
					},
					selected: {
						fillColor: '#FF5722',
						fillOpacity: 0.45,
						stroke: true,
						fill: true,
						color: '#aaa',
						opacity: 1,
						weight: 1,
					},
					default: {
						fillColor: '#777',
						fillOpacity: 0.2,
						stroke: true,
						fill: true,
						color: '#ccc',
						opacity: 1,
						weight: 1,
					}
				},
				autoStyleField: '',
				activeZip: null,
				selectionMode: 'filter',
				filteredZips: [],
				makingScreenshot: 0
			};
		},
		computed: {
			isMetricSystem() {
				var language = window.navigator.userLanguage || window.navigator.language;
				return !language || (language.toLowerCase().indexOf('us') === -1 && language.toLowerCase().indexOf('gb') === -1);
			},
			metricMultiplier() {
				return this.isMetricSystem ? 1000 : 1609.344;
			},
			statistics() {
				return this.filteredZips.some(f => f.selected) ? this.filteredZips.filter(f => f.selected).map(f => f.Props) : this.zipData.map(f => f.Props);
			},
			autoStyleMax() {
				return (_.maxBy(this.statistics, v => Math.max(0, v[this.autoStyleField])) || {})[this.autoStyleField];
			},
			autoStyleMin() {
				return Math.max(0, (_.minBy(this.statistics, v => Math.max(0, v[this.autoStyleField])) || {})[this.autoStyleField]);
			},
			autoStyleMedian() {
				return this.median(this.statistics.map(f => Math.max(0, f[this.autoStyleField])));
			},
			plainItems() {
				let items = [];

				let getItems = (v, parent) => {
					if (!v) return;
					if (!v.items) {
						items.push(v)
					} else {
						v.items.map(f => getItems(f, v));
					}
				}

				getItems(this.config?.layout);

				return items;
			},
			plainItemsGouped() {
				let items = [];

				let getItems = (v, parent) => {
					if (!v) return;
					if (!v.items || (v.type === 'group')) {
						items.push(v)
					} else {
						v.items.map(f => getItems(f, v));
					}
				}

				getItems(this.config?.layout);

				return items;
			},
			autoStyleFieldCaption() {
				return this.plainItems.find(f => f.fieldName === this.autoStyleField)?.caption;
			},
			hasSelection() {
				return !!this.filteredZips.length;
			}
		},
		watch: {
			filtersData() {
				this.applyFilters();
			},
			filterAreaWkts() {
				this.loadZipData();
			},
			autoStyleField() {
				this.refreshStyles();
			},
			filterAreaNames() {
				this.loadAreaByName();
			},
			filteredZips() {
				if (!this.$isInitializing) {
					let zips = this.zipData.filter(f => f.selected);
					let filteredPopulation = Math.round(_.sumBy(zips, f => parseFloat(f.PopulationSize)));
					this.$emit('zips-change', this.zipData.filter(f => f.selected).map(f => f.ZipCode), filteredPopulation);
				}
			},
			config(v) {
				this.applyConfig(v);
			},
			locations(v) {
				this.loadLocations(v);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.zipsMap = this;
			dynamic.loadOnce([
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.js',
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.css',
			]).then(() => {
				var sources = [
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.js',
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.css',
					'https://unpkg.com/leaflet.vectorgrid@latest/dist/Leaflet.VectorGrid.bundled.js'
				];
				dynamic.loadOnce(sources).then(() => {
					this.buildMap();
					setImmediate(async () => {
						if (this._isDestroyed) return;
					});
				});
			});

			this.$unobserve = resizeObserver.observe(this, this.$el, this.onResize);

			if (this.locations?.length) {
				this.loadLocations(this.locations);
			}

			this.$eventsBus.$on('zips-map-auto-style-field-changed', this.onAutoStyleFieldChange);
			this.$eventsBus.$on('zips-map--group-statistics', this.onGroupSummaryCalculation)

			if (this.config) {
				this.applyConfig(this.config);
			}
		},
		destroyed() {
			this.$eventsBus.$off('zips-map-auto-style-field-changed', this.onAutoStyleFieldChange);
			this.$eventsBus.$off('zips-map--group-statistics', this.onGroupSummaryCalculation)
			delete this.$layerZipSelection;
			delete this.$map;
			if (this.$unobserve)
				this.$unobserve();
			delete this.$unobserve;
			delete this.$isInitializing;
		},
		methods: {
			onGroupSummaryCalculation(e) {
				this.$emit('group-summary-calculated', e);
			},
			toggleZipSelection(zip) {
				let firstSelection = !this.hasSelection;
				this.$set(zip, 'selected', !zip.selected);
				

				if (firstSelection && zip.selected) {
					this.refreshStyles();
				} else {
					this.$vectorGrid.setFeatureStyle(zip.Id, this.getAutoStyle(zip));
				}

				this.activeZip = null;
			},
			applyConfig(cfg) {
				this.config = cfg;
				this.autoStyleField = cfg?.defaultAutoStyleField;
				this.$emit('config-loaded', cfg);
			},
			median(values) {
				if (values.length === 0) {
					return 0;
				}

				values = [...values].sort((a, b) => a - b);

				const half = Math.floor(values.length / 2);

				return (values.length % 2
					? values[half]
					: (values[half - 1] + values[half]) / 2
				);
			},
			onAutoStyleFieldChange(field) {
				if (__DEBUG__)
					console.debug('zips-map-auto-style-field-changed', { field });
				this.autoStyleField = field.fieldName;
			},
			applyFilters() {
				if (!this.filtersData.length) return;

				let filteredZips = this.zipData.filter(z => {
					let polygon = (z.geoJson?.geometries || [])[0] || z.geoJson;
					let result = polygon && this.filtersData.some(f => {
						try {
							return intersect(polygon, f.polygon);
						} catch {
							if (__DEBUG__)
								console.error('filtering', { z, f });
							return false;
						}
					});

					if (result) {
						this.$set(z, 'selected', this.selectionMode === 'filter');
					}
					return result;
				});

				if (__DEBUG__)
					console.debug('applyFilters', { filteredZips });

				this.filteredZips = this.zipData.filter(f => f.selected !== null);
				this.filtersData = [];
				this.$filtersDrawnItems.clearLayers();

				this.$nextTick(() => {
					this.refreshStyles();
					this.$nextTick(() => {
						this.$vectorGrid.redraw();
					});
				});
			},
			clearMap() {
				if (this.$vectorGrid) {
					this.$vectorGrid.remove();
				}
				this.zipData = [];
			},
			async loadLocations(locations) {
				if (!locations?.length) {
					this.clearMap();
					return;
				}
				let areaNames = locations.filter(f => !f.radius && f.place?.formatted_address).map(f => f.place?.formatted_address);

				let areaWkts = this.loadAreaByName(areaNames);

				await Promise.all([areaWkts]);

				let radiusLocations = locations.filter(f => f.radius).map(f => {
					let polygon = circleToPolygon([f.location.lng, f.location.lat], parseInt(f.radius), { numberOfEdges: 32 });
					let wktObject = new wkt.Wkt();
					wktObject.fromJson(polygon);
					return wktObject.toString();
				});
				
				let filterAreaWkts = [
					...(await areaWkts),
					...radiusLocations
				];

				this.filterAreaWkts = filterAreaWkts;
			},
			async loadAreaByName(filterAreaNames) {
				if (!filterAreaNames?.length) return [];

				this.isLoading++;
				const areas = await Promise.all(filterAreaNames.map(f => this.$Data.getLocationGeoJson({ q: f }))).finally(() => this.isLoading--);

				let filteredWkts = areas.map(area => {
					if (area && area[0].geojson) {
						let wktObject = new wkt.Wkt();
						wktObject.fromJson(area[0].geojson);
						return wktObject.toString();
					}
				}).filter(f => f);

				return filteredWkts;
			},
			fitDrawnItems() {
				const bounds = this.$zipsItems.getBounds();
				if (!bounds.isValid()) return;
				setImmediate(() => {
					this.$map.fitBounds(bounds);
				});
			},
			buildMap() {
				if (__DEBUG__)
					console.log('Build map');

				const L = window.L;

				const map = this.$map = L.map(this.$refs.map).setView([this.mapPosition.lat || 37.8, this.mapPosition.lng || -96], this.mapPosition.zoom || 5);

				this.$filtersDrawnItems = L.featureGroup().addTo(map);//L.geoJson().addTo(map);
				if (this.showFilters && !this.readonly) {
					map.addControl(new L.Control.Draw({
						zIndex: 1,
						edit: false,/*{
							featureGroup: this.$filtersDrawnItems
						},*/
						draw: {
							circlemarker: false,
							marker: false,
							polyline: false
						}
					}));

					map.on('draw:created', (event) => {
						var layer = event.layer;
						layer.options.fillColor = 'none';
						layer.options.weight = 2;
						layer.options.color = '#333';
						layer.options.opacity = 0.3;

						if (__DEBUG__)
							console.debug('draw:created', { event });

						let polygonFunc = null;
						if (event.layerType === 'circle') {
							polygonFunc = (layer) => circleToPolygon([layer._latlng.lng, layer._latlng.lat], parseInt(layer._mRadius), { numberOfEdges: 32 });
						} else {
							polygonFunc = (layer) => layer.toGeoJSON()?.geometry;
						}

						let polygon = polygonFunc(event.layer);

						let wktObject = new wkt.Wkt();
						wktObject.fromJson(polygon);
						
						if (!this.allowMultipleFilters) {
							this.$filtersDrawnItems.clearLayers();
							this.filtersData = [];
						}
						this.$filtersDrawnItems.addLayer(layer);

						this.filtersData.push({
							layer,
							polygon,
							event,
							wkt: wktObject.toString(),
							polygonFunc
						});
					});

					map.on('draw:edited', (event) => {
						var layers = event.layers;
						layers.eachLayer((layer) => {
							let existedData = this.filtersData.find(f => f.layer === layer);
							if (existedData) {
								existedData.polygon = existedData.polygonFunc(layer);

								let wktObject = new wkt.Wkt();
								wktObject.fromJson(existedData.polygon);
								existedData.wkt = wktObject.toString();
							}
							if (__DEBUG__)
								console.debug('draw:edited', { existedData, event, layer });
						});
					});

					map.on('draw:deleted', (event) => {
						var layers = event.layers;
						layers.eachLayer((layer) => {
							let existedData = this.filtersData.findIndex(f => f.layer === layer);
							if (existedData > -1) {
								this.filtersData.splice(existedData, 1);
							}
						});
					});

					this.$el.querySelector('.leaflet-top').appendChild(this.$refs.toolbarActions);
				}

				L.tileLayer(
					'https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png',
					{ maxZoom: 100 }
				).addTo(map);
				
				this.$drawnItems = L.featureGroup();
				this.$zipsItems = L.featureGroup();

				this.$layerGroup = L.featureGroup([this.$drawnItems, this.$zipsItems]);

				this.$layerGroup.addTo(map);

				this.$emit('built');

				if (!this.locations?.length) {
					this.fitUserLocation();
				}
			},
			async loadZipData() {
				this.$isInitializing = true;
				if (!this.filterAreaWkts?.length) {
					this.zipData = [];
					this.buildZipsDrawings();
					return;
				}
				this.isLoading++;
				this.zipData = (await this.$Data.postExternalApi('ZipGeoData/list', {
					zips: [],
					countryCodes: [this.countryCode],
					wktPolygons: this.filterAreaWkts,
					returnProps: true,
					returnPolygons: true,
					polygonSize: 1
				}).finally(() => this.isLoading--) || []).filter(f => f.PopulationSize > 0 && f.Props).map(f => {
					f.selected = this.selectedZips && this.selectedZips.includes(f.ZipCode);
					return f;
				});
				this.filteredZips = this.zipData.filter(f => f.selected);

				this.buildZipsDrawings();

				this.$nextTick(() => this.$isInitializing = false);

				this.$emit('zips-loaded');
			},
			getAutoStyle(zip) {
				let data = zip.Props;
				let value = data[this.autoStyleField] || 0;
				let percentage = Math.max(0, Math.min(1, 1 - (value - this.autoStyleMin) / (this.autoStyleMax - this.autoStyleMin)));
				
				let colorIndex = Math.max(0, Math.min(this.config.palette.length - 1, Math.floor(this.config.palette.length * percentage)));

				if (this.hasSelection && !zip.selected) {
					return this.styles.default;
				}

				return {
					fillColor: this.config.palette[colorIndex],
					fillOpacity: 0.8,
					stroke: true,
					fill: true,
					color: `${this.config.palette[colorIndex]}`,
					opacity: 1,
					weight: 1,
				}
			},
			refreshStyles() {
				this.zipData.map(f => {
					this.$vectorGrid.setFeatureStyle(f.Id, this.getAutoStyle(f));
				});
			},
			buildZipsDrawings() {
				if (__DEBUG__)
					console.info('buildZipsDrawings');

				if (!this.$zipsItems) return;
				this.$zipsItems.clearLayers();
				const L = window.L;

				
				let getJsonDoc = {
					type: 'FeatureCollection',
					features: []
				};

				let highlight = null;
				var clearHighlight = () => {
					if (highlight?.Id) {
						let selected = this.zipData.find(f => f.Id === highlight.Id);
						this.$vectorGrid.setFeatureStyle(highlight.Id, this.getAutoStyle(selected));
					}
					highlight = null;
				};

				this.zipData.map(zip => {
					if (!zip.PopulationSize) return;
					var geoJson = null;
					try {
						let wktString = zip.GeoPolygonWkt || '';
						if (wktString.indexOf())
						geoJson = parseWkt(zip.GeoPolygonWkt)

						if (geoJson.type === 'GeometryCollection') {
							geoJson.geometries = geoJson.geometries.filter(f => f.type === 'Polygon');
						}

						let feature = {
							type:"Feature",
							properties: zip,
							geometry: geoJson
						}
						getJsonDoc.features.push(feature);

						zip.geoJson = geoJson;
						try {
							zip.Props = JSON.parse(zip.Props);
						} catch(ex) {
							if (__DEBUG__)
								console.error(ex, zip);
						}
					} catch(ex) {
						if (__DEBUG__)
							console.error(ex, zip);
					}
				});

				if (this.$vectorGrid) {
					this.$vectorGrid.remove();
				}

				this.$zipsJson = getJsonDoc;
				var feature = L.geoJson(getJsonDoc);
				this.$zipsBounds = feature.getBounds();
				
				this.$vectorGrid = L.vectorGrid.slicer(getJsonDoc, {
					rendererFactory: L.canvas.tile,
					tolerance: 10,
					vectorTileLayerStyles: {
						sliced: (properties, zoom) => {
							return this.getAutoStyle(properties);
						}
					},
					interactive: true,
					getFeatureId: function(f) {
						return f.properties.Id;
					}
				}).on('mouseover', (e) => {
					var data = e.layer.properties;

					clearHighlight();
					highlight = data;

					let zip = this.zipData.find(f => f.Id === data?.Id);
					let autoStyle = this.getAutoStyle(zip);

					this.$vectorGrid.setFeatureStyle(data.Id, {
						...this.styles.highlight,
						fillColor: autoStyle.fillColor,
						fillOpacity: autoStyle.fillOpacity
					});
				}).on('mouseout', (e) => {
					clearHighlight();
				}).on('click', e => {
					var data = e.layer.properties;
					this.activeZip = null;
					this.$nextTick(() => {
						this.activeZip = this.zipData.find(f => f.Id === data?.Id);
					});
				}).addTo(this.$map);

				if (this.$zipsBounds) {
					this.$map.fitBounds(this.$zipsBounds);
				}
			},
			onResize() {
				if (this.$map?._onResize) {
					this.$map._onResize();
				}
			},
			onImageGenerated(dataURI) {
				this.$emit('image-generated', dataURI);
				if (this.makingScreenshot === 1) {
					this.makingScreenshot = 0;
				} else {
					this.makingScreenshot = 0;
					this.$nextTick(() => this.makingScreenshot++);
				}
			},
			generateScreen() {
				this.makingScreenshot++;
			},
			async fitUserLocation() {
				let userLocationInfo = await this.$auth.getUserLocation();

				const bounds = userLocationInfo.bounds;
				if (bounds) {
					this.$map.fitBounds(
						[
							[bounds[0], bounds[2]],
							[bounds[1], bounds[3]]
						]
					);
				}
			}
		}
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.maps--zips-map {
		.map-holder {
			height: calc(80vh - var(--top-offset) / 2 - 20px);
			width: 100%;
			min-height: 240px;
			margin-top: 0px;
			position: relative;

			&.has-zips {
				height: calc(50vh - var(--top-offset) / 2 - 20px);
			}

			&:not(.has-filters) {
				.leaflet-draw {
					display: none;
				}

				.toolbar-actions {
					display: none;
				}
			}

			.toolbar-actions {
				.action.active {
					background: $base-accent;
					color: $base-bg;
				}
			}

			.l1eaflet-draw-section:nth-child(2) {
				display: none;
			}

			.map {
				width: 100%;
				height: 100%;
				min-height: 240px;
				min-width: 240px;
			}

			> .actions-group {
				position: absolute;
				left: 60px;
				top: 10px;
				z-index: 1000;
				padding: 10px;
				background: rgba(255, 255, 255, 0.75);
				box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
				border-radius: 4px;
			}

			> .buttons-group {
				z-index: 1000;
				position: absolute;
				left: 10px;
				bottom: 10px;
			}

			.region-input {

			}

			.toolbar-actions {
				.action {
					color: #555;
				}
			}
		}

		.leaflet-interactive {
			pointer-events: none;
		}

		.bottom-panel {
			position: absolute;
			bottom: 4px;
			left: 14px;
			z-index: 1000;
			font-size: 10px;
		}
	}

	.pac-container {
		z-index: 2000 !important;
	}
</style>
